import { useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Alert, Box, PDCAlpha as alpha, Tooltip, Typography } from "@mzc-pdc/ui";
import { IconCompleteCircle, IconInfoCircle, IconWarningTriangle } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";

/**
 * 브라우저가 «직접 전송 중»인 업로드 탭 상단의 이탈 안내 배너.
 *
 * 자산 작업 탭(자산 생성)과 저장소 업로드 탭이 같은 문장을 쓴다 — 두 큐 모두 페이지를 닫으면
 * 그 자리에서 전송이 끊긴다. 브라우저 경고창은 문구를 브라우저가 통제해 「무엇을 잃는지」를
 * 말할 수 없으므로 화면 안에 안내를 따로 둔다. 배치 근거는
 * docs/design-handoff/DAM-607-design/README.md 참고.
 *
 * 전송 중 개수는 «잡» 단위로 센다(이탈로 잃는 단위가 잡이라서). 판정식은 부르는 쪽이 정하고
 * (hooks/useMyJobs.js 의 isJobProcessing 과 같다) 여기는 그 결과를 그리기만 한다.
 */
// active 기본값은 true — 생략하면 latch 가 매 렌더 비워져 해제 문구가 영영 뜨지 않는다.
const UploadLeaveGuide = ({ uploadingKeys, transferredKeys, active = true, doneDescription = null }) => {
    const { t } = useTranslation();

    const infoIconRef = useRef(null);

    //Note: 해제 문구(「이제 닫아도 됩니다」)는 패널을 닫을 때까지 남긴다 — 수십 분 걸리는 작업이라
    //몇 초 뒤 사라지면 돌아온 사람이 못 본다. 패널을 닫아도 언마운트되지 않으므로(RightSideBar 는 CSS
    //클래스만 토글하고 TabPanel 은 hidden 으로 감춘다) 마운트 수명 대신 active 를 초기화 신호로 쓴다.
    //사이드바가 다른 용도로 열리면(type 변경) 언마운트돼 latch 가 풀린다 — 해제 문구를 놓칠 뿐 위험하지 않다.
    //
    //개수는 큐 길이가 아니라 «이번 구간에 전송을 본 잡»으로 센다. 큐는 완료돼도 비워지지 않아
    //(removeQueue 는 업로드 완료 경로에서 불리지 않는다) 길이를 쓰면 세션 중 쌓인 과거 잡까지 세어진다.
    const seenKeysRef = useRef(new Set());
    if (!active) seenKeysRef.current.clear();
    else uploadingKeys.forEach((key) => seenKeysRef.current.add(key));

    const uploadingCount = uploadingKeys.length;

    //Note: latch 는 「이번 구간에 봤는가」만 가르고, 개수는 «성공으로 끝난» 잡만 센다.
    //큐 항목은 들어가자마자 UPLOADING 이라 「아직 안 끝났다」로 세면 취소·실패한 잡까지 들어가,
    //한 바이트도 안 갔는데 「전송 완료」라고 말하게 된다(저장소 업로드는 카드에 취소 버튼이 있어 흔하다).
    const transferredCount = transferredKeys.filter((key) => seenKeysRef.current.has(key)).length;

    const guide =
        uploadingCount > 0
            ? {
                  severity: "warning",
                  accent: theme.palette.warning[700],
                  icon: <IconWarningTriangle size={16} htmlColor={theme.palette.warning[700]} />,
                  title: t(`common::msg::${"{{count}} upload(s) transferring — do not close or reload this page"}`, {
                      count: uploadingCount,
                  }),
                  description: t(
                      `common::msg::${"Moving between menus or closing this panel is fine."}`,
                      `Moving between menus or closing this panel is fine.`,
                  ),
              }
            : transferredCount > 0
            ? {
                  severity: "success",
                  accent: theme.palette.success[700],
                  icon: <IconCompleteCircle size={16} htmlColor={theme.palette.success[700]} />,
                  title: t(`common::msg::${"{{count}} upload(s) transferred — you can close this page"}`, {
                      count: transferredCount,
                  }),
                  // 부연이 없으면 툴팁과 ⓘ 를 통째로 뺀다. 전송으로 끝나는 업로드(저장소)에서는
                  // 「서버에서 계속됩니다」가 사실이 아니라 덧붙일 말이 없다.
                  description: doneDescription ? t(`common::msg::${doneDescription}`, doneDescription) : null,
              }
            : null;

    if (!guide) return null;

    const banner = (
        <Alert
            tabIndex={0}
            severity={guide.severity}
            variant={"outlined"}
            icon={guide.icon}
            role={"status"}
            sx={{
                borderRadius: 0,
                borderWidth: "0 0 1px 0",
                alignItems: "flex-start",
                px: 1.5,
                py: 1,
                //Note: 테마의 outlined Alert 는 severity 를 색으로 구분하지 않아(warning·success 가
                //배경·테두리 모두 동일) accent 를 직접 입힌다. 색만으로 가르지 않도록 아이콘과 문장이 같은 말을 한다.
                bgcolor: alpha(guide.accent, 0.08),
                borderColor: alpha(guide.accent, 0.4),
                ".MuiAlert-icon": { mt: "2px", mr: 1, p: 0 },
                //Note: 이 저장소는 .MuiAlert-message 를 flex-direction: row 로 덮어 제목과 부연이
                //가로로 늘어선다(MUI 기본은 block). 여기서 되돌린다. flexGrow 가 없으면 flex 자식이
                //내용 폭으로 줄어 문구가 일찍 줄바꿈된다.
                ".MuiAlert-message": { p: 0, display: "block", flexGrow: 1, minWidth: 0 },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant={"body3_500"} color={"greySecondary.800"} component={"p"}>
                    {guide.title}
                </Typography>
                {/* 「여기 더 설명이 있다」는 단서 — 없으면 툴팁을 못 찾는다. 배너는 「닫지 마세요」라고
                    하는데 패널 우상단에는 닫기 버튼이 있어서, 「패널은 닫아도 된다」가 툴팁에만 있으면
                    그 모순이 안 풀린다. 장식이므로 스크린리더에서 숨긴다(같은 문장이 aria-describedby 로 붙는다). */}
                {guide.description && (
                    <Box
                        component={"span"}
                        ref={infoIconRef}
                        aria-hidden={"true"}
                        sx={{
                            display: "inline-flex",
                            flexShrink: 0,
                            //Note: 줄 상자가 아니라 글자 중심에 맞춘다. 한글 잉크가 줄 상자 안에서
                            //아래쪽에 앉아 alignItems:center 만으로는 아이콘이 1px 떠 보인다.
                            transform: "translateY(1px)",
                        }}
                    >
                        <IconInfoCircle size={12} htmlColor={theme.palette.greySecondary[500]} />
                    </Box>
                )}
            </Box>
        </Alert>
    );

    if (!guide.description) return banner;

    /* 부연은 상시 노출하지 않는다 — 다국어에서 두 문장이 480px 패널을 넘긴다.
       describeChild 필수 — 없으면 Tooltip 이 자식의 aria-label 을 덮어써서 스크린리더가
       배너 본문(경고)을 못 읽고 부연만 읽는다. describeChild 는 aria-describedby 로 덧붙는다.
       anchorEl 을 ⓘ 로 고정한다 — 기본값인 트리거(480px 배너) 기준이면 필터 줄까지 내려간다. */
    return (
        <Tooltip
            title={guide.description}
            describeChild
            placement={"bottom"}
            PopperProps={{ anchorEl: () => infoIconRef.current }}
        >
            {banner}
        </Tooltip>
    );
};

UploadLeaveGuide.propTypes = {
    /** 지금 전송 중인 잡의 키 목록. 길이가 「전송 중 N개」이고, 이번 구간에 본 잡을 여기서 누적한다. */
    uploadingKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
    /** 성공으로 끝난 잡의 키 목록. 이 중 이번 구간에 본 것만 해제 문구의 N 이 된다. */
    transferredKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
    /** 잡 패널이 열려 있는가. 닫히면 해제 문구 latch 를 비운다. */
    active: PropTypes.bool,
    /** 해제 문구의 부연. 없으면 툴팁과 ⓘ 를 렌더하지 않는다. */
    doneDescription: PropTypes.string,
};

export default UploadLeaveGuide;
